import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { AdminBadge } from '@/components/layout/AdminBadge';
import { ThemeToggle } from '@/components/layout/ThemeToggle';
import { UserMenu } from '@/components/layout/UserMenu';
import { setActiveOrgSlug, useActiveOrg } from '@/lib/activeOrg';
import { type MeResponse } from '@/lib/auth';
import { cn } from '@/lib/cn';

interface HeaderProps {
  me: MeResponse;
}

export function Header({ me }: HeaderProps) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [orgPickerOpen, setOrgPickerOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const orgs = me.organizations;
  const activeOrg = useActiveOrg(orgs);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOrgPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const switchOrg = (slug: string) => {
    setActiveOrgSlug(slug);
    setOrgPickerOpen(false);
    // Drop every cached query — they're org-scoped under the hood. Cheaper
    // and safer than enumerating which keys to invalidate.
    qc.removeQueries({
      predicate: (q) =>
        Array.isArray(q.queryKey) &&
        q.queryKey[0] !== 'me' &&
        q.queryKey[0] !== 'distributors' &&
        q.queryKey[0] !== 'health' &&
        !(q.queryKey[0] === 'settings'),
    });
  };

  return (
    <header className="border-b border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 dark:border-stone-800 dark:bg-stone-900">
      <div className="mx-auto flex h-14 max-w-7xl items-center justify-between px-4">
        <div className="flex items-center gap-6">
          <Link
            to="/"
            className="text-lg font-semibold tracking-tight text-forge-700 dark:text-forge-400"
          >
            PartForge
          </Link>
          <nav className="flex items-center gap-4 text-sm text-stone-700 dark:text-stone-300">
            <Link to="/" className="hover:text-stone-900 dark:hover:text-stone-100">
              Dashboard
            </Link>
            <Link to="/parts" className="hover:text-stone-900 dark:hover:text-stone-100">
              Parts
            </Link>
            <Link to="/locations" className="hover:text-stone-900 dark:hover:text-stone-100">
              Locations
            </Link>
            <Link to="/boms" className="hover:text-stone-900 dark:hover:text-stone-100">
              BOMs
            </Link>
          </nav>
        </div>

        <div className="flex items-center gap-3">
          <AdminBadge me={me} />
          <ThemeToggle />
          <div className="relative" ref={ref}>
            <button
              type="button"
              onClick={() => setOrgPickerOpen((v) => !v)}
              className={cn(
                'rounded-md border border-stone-200 dark:border-stone-800 bg-stone-50 dark:bg-stone-900 px-3 py-1.5 text-sm',
                'hover:bg-stone-100 dark:hover:bg-stone-800',
                'dark:border-stone-700 dark:bg-stone-800 dark:hover:bg-stone-700',
              )}
            >
              <span className="text-stone-500 dark:text-stone-400">Org:</span>{' '}
              <span className="font-medium">{activeOrg?.name ?? '—'}</span>
              <span className="ml-1 text-stone-400 dark:text-stone-500">▾</span>
            </button>
            {orgPickerOpen && (
              <div className="absolute right-0 z-30 mt-1 w-80 rounded-md border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 shadow-md dark:border-stone-700 dark:bg-stone-900">
                <ul className="max-h-64 overflow-y-auto py-1 text-sm">
                  {orgs.map((org) => (
                    <li key={org.id}>
                      <button
                        type="button"
                        onClick={() => switchOrg(org.slug)}
                        className={cn(
                          'flex w-full items-center justify-between px-3 py-2 text-left',
                          'hover:bg-forge-50 dark:hover:bg-stone-800',
                          activeOrg?.id === org.id ? 'bg-stone-50 dark:bg-stone-900 dark:bg-stone-800' : '',
                        )}
                      >
                        <span className="flex items-center gap-2">
                          <span className="font-medium text-stone-800 dark:text-stone-200 dark:text-stone-100">{org.name}</span>
                        </span>
                        <span className="text-xs text-stone-400 dark:text-stone-500">{org.role}</span>
                      </button>
                    </li>
                  ))}
                </ul>
                <div className="border-t border-stone-200 dark:border-stone-800 px-3 py-2 dark:border-stone-700">
                  <button
                    type="button"
                    onClick={() => {
                      setOrgPickerOpen(false);
                      navigate('/orgs/new');
                    }}
                    className="flex w-full items-center justify-between rounded text-left text-sm text-forge-700 dark:text-forge-400 hover:underline dark:text-forge-400"
                  >
                    + Create organization
                  </button>
                </div>
              </div>
            )}
          </div>

          <UserMenu me={me} />
        </div>
      </div>
    </header>
  );
}
